import React, { createContext, useContext, useEffect, useState } from 'react';

// Temanın alabileceği durumları belirliyoruz.
export type Theme = 'dark' | 'light' | 'system';

type ThemeProviderProps = {
    children: React.ReactNode;
    defaultTheme?: Theme;
    storageKey?: string;
};

type ThemeProviderState = {
    theme: Theme;
    setTheme: (theme: Theme) => void;
};

// Başlangıç state'i (Default state)
const initialState: ThemeProviderState = {
    theme: 'system',
    setTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
                                  children,
                                  defaultTheme = 'system',
                                  storageKey = 'app-ui-theme',
                                  ...props
                              }: ThemeProviderProps) {
    // State'i başlatırken lazy initialization kullanıyoruz.
    // Böylece localStorage okuması sadece ilk render'da yapılır, performansı artırır.
    const [theme, setTheme] = useState<Theme>(
        () => (localStorage.getItem(storageKey) as Theme) || defaultTheme
    );

    useEffect(() => {
        const root = window.document.documentElement;

        // Her tema değişiminde önce eski class'ları temizliyoruz.
        root.classList.remove('light', 'dark');

        if (theme === 'system') {
            // Eğer sistem seçiliyse, işletim sisteminin tercihini kontrol et
            const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
                ? 'dark'
                : 'light';

            root.classList.add(systemTheme);
            return;
        }

        // Seçilen temayı HTML root elementine ekle
        root.classList.add(theme);
    }, [theme]); // theme state'i değiştiğinde effect yeniden tetiklenir

    const value = {
        theme,
        setTheme: (theme: Theme) => {
            localStorage.setItem(storageKey, theme); // Seçimi kaydet
            setTheme(theme); // State'i güncelle
        },
    };

    return (
        <ThemeProviderContext.Provider {...props} value={value}>
            {children}
        </ThemeProviderContext.Provider>
    );
}

// ---------------------------------------------------------
// Custom Hook: Tema verisini tüketmek için kullanılır.
// ---------------------------------------------------------
export const useTheme = () => {
    const context = useContext(ThemeProviderContext);

    // Eğer hook Provider dışında kullanılırsa developer'ı uyar.
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }

    return context;
};